import {useCallback, useEffect, useRef, useState} from 'react';

export type GeolocationPermission =
  /** Still finding out */
  | 'checking'
  | 'granted'
  /** The browser will ask the user (or we don't know, e.g. Safari) */
  | 'prompt'
  | 'denied'
  /** No geolocation API, or the page is not served over a secure context (HTTPS) */
  | 'unsupported';

export interface GeolocationFix {
  latitude: number;
  longitude: number;
  /** Radius of uncertainty, in meters */
  accuracy: number;
  /** Time (ms since epoch) when the position was obtained */
  timestamp: number;
}

const WATCH_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  // Always use fresh readings
  maximumAge: 0,
  timeout: 20000,
};

const PERMISSION_DENIED = 1;

const isGeolocationSupported = (): boolean =>
  typeof navigator !== 'undefined' && 'geolocation' in navigator && window.isSecureContext;

const toFix = (position: GeolocationPosition): GeolocationFix => ({
  latitude: position.coords.latitude,
  longitude: position.coords.longitude,
  accuracy: position.coords.accuracy,
  timestamp: position.timestamp,
});

/**
 * Gives the permission state of the geolocation and, once granted, keeps the most recent high accuracy
 * position while the component is mounted.
 */
export const useGeolocation = () => {
  const [permission, setPermission] = useState<GeolocationPermission>(
    isGeolocationSupported() ? 'checking' : 'unsupported'
  );
  const [position, setPosition] = useState<GeolocationFix | undefined>(undefined);
  /** Last error that is not about the permission (position unavailable or timeout) */
  const [error, setError] = useState<GeolocationPositionError | undefined>(undefined);
  const watchIdRef = useRef<number | undefined>(undefined);

  const stopWatching = useCallback(() => {
    if (watchIdRef.current !== undefined) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = undefined;
    }
  }, []);

  const startWatching = useCallback(() => {
    if (watchIdRef.current !== undefined) {
      return;
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      (newPosition) => {
        setError(undefined);
        setPosition(toFix(newPosition));
      },
      (positionError) => {
        if (positionError.code === PERMISSION_DENIED) {
          stopWatching();
          setPermission('denied');
        } else {
          setError(positionError);
        }
      },
      WATCH_OPTIONS
    );
  }, [stopWatching]);

  // Find out the permission state and follow its changes
  useEffect(() => {
    if (!isGeolocationSupported()) {
      return;
    }

    let permissionStatus: PermissionStatus | undefined;
    let isMounted = true;

    const applyState = (state: PermissionState) => {
      if (!isMounted) return;
      setPermission(state === 'granted' ? 'granted' : state === 'denied' ? 'denied' : 'prompt');
    };

    if (!navigator.permissions?.query) {
      // E.g. Safari: we can only find out by asking
      setPermission('prompt');
    } else {
      navigator.permissions
        .query({name: 'geolocation'})
        .then((status) => {
          permissionStatus = status;
          applyState(status.state);
          status.onchange = () => applyState(status.state);
        })
        .catch(() => setPermission('prompt'));
    }

    return () => {
      isMounted = false;
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, []);

  // Watch the position while the permission is granted
  useEffect(() => {
    if (permission === 'granted') {
      startWatching();
    } else {
      stopWatching();
    }

    return stopWatching;
  }, [permission, startWatching, stopWatching]);

  /** Makes the browser ask the user for the permission (it can't be asked again once denied) */
  const requestPermission = useCallback(() => {
    if (!isGeolocationSupported()) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (newPosition) => {
        setPosition(toFix(newPosition));
        setError(undefined);
        setPermission('granted');
      },
      (positionError) => {
        if (positionError.code === PERMISSION_DENIED) {
          setPermission('denied');
        } else {
          // The permission was given, but the position could not be obtained yet
          setError(positionError);
          setPermission('granted');
        }
      },
      WATCH_OPTIONS
    );
  }, []);

  return {permission, position, error, requestPermission};
};
